import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getMe } from "@elation/api-client/src/stitch-web";

import { navigationV2 } from "@src/shared/navigation/v2";
import { toErrorMessage } from "@src/shared/utils/errors";
import {
  events,
  type LoginMethod,
} from "@src/shared/utils/observability/events";
import {
  trackSessionInitFailure,
  trackUserLoggedIn,
} from "@src/shared/utils/observability/trackUserLoggedIn";

import { resolvePostAuthDestinationV2 } from "../../../application/v2";
import { UnauthenticatedLayoutV2 } from "../../../ui/layout/v2";
import { LoginCallbackPageV2 } from "../../../ui/pages/v2/LoginCallbackPage";

export function LoginCallbackV2Route() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [postCallbackError, setPostCallbackError] = useState<string | null>(
    null,
  );

  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");
  const redirectTo = searchParams.get("redirectTo");
  // The Capacitor mobile handoff routes failures here too; `method` lets the
  // `login failed` metric distinguish them from the web OAuth path.
  const method: LoginMethod =
    searchParams.get("method") === "okta_mobile" ? "okta_mobile" : "okta_web";

  useEffect(() => {
    if (error) {
      events.loginFailed.track({
        method,
        error: errorDescription ?? error,
        error_code: error,
      });
      setIsProcessing(false);
      return;
    }

    const completeLogin = async () => {
      try {
        const [{ data: me }, destination] = await Promise.all([
          getMe(),
          resolvePostAuthDestinationV2({
            fallback: navigationV2.home(),
            redirectTo,
            route: "v2/login/callback",
            operation: "oktaCallback",
          }),
        ]);
        trackUserLoggedIn(me, method);
        navigate(destination, { replace: true });
      } catch (err) {
        console.error("Error fetching /me after OAuth callback:", err);
        setPostCallbackError(
          toErrorMessage(err, "Sign-in failed after OAuth callback"),
        );
        trackSessionInitFailure(method, err);
        setIsProcessing(false);
      }
    };

    void completeLogin();
  }, [error, errorDescription, redirectTo, navigate, method]);

  const handleRetry = () => {
    navigate(navigationV2.login(), { replace: true });
  };

  return (
    <UnauthenticatedLayoutV2>
      <LoginCallbackPageV2
        error={error ?? postCallbackError ?? undefined}
        errorDescription={errorDescription || undefined}
        isLoading={isProcessing}
        onRetry={handleRetry}
      />
    </UnauthenticatedLayoutV2>
  );
}
